// Queue scope engine (F-SCOPE-*). Ported verbatim from legacy loadQueueScope/
// saveQueueScope/getQueueScope/scopeMatch/scopeLabel (v20 lines ~4520-4545).
//
// Scope decides WHOSE work a nurse sees on Today / Board / Patients:
//   mine        — cases assigned to you
//   mine_plus   — yours + hand-picked colleagues (extra: [user_id])
//   all         — everyone
//   unassigned  — the "Legacy pool": ownerless rows (drain target for coords)
//
// PERSISTENCE PARITY (load-bearing): the storage KEY and the stored MODE TOKEN
// both match v_20 exactly, because 2.0 and v_20 run against the same DB during
// the parallel phase and read/write the SAME 'tumaini_queue_scope_<uid>' entry.
// The legacy token for the pool is 'unassigned' — so that is the canonical
// stored value here too. The Blueprint/Task label this scope "Legacy pool" and
// refer to it as 'legacy'; normalizeMode() accepts 'legacy' as an INPUT alias
// so callers may pass either, but what we PERSIST stays 'unassigned' and a
// v_20 tab reading the key still recognises it. Diverging the token would
// silently reset a nurse's scope every time she switched apps.

import { canAssign } from '../auth.js';

export const SCOPE_KEY_PREFIX = 'tumaini_queue_scope_'; // EXACT legacy key prefix

// Canonical mode → label (legacy QS_MODES). 'unassigned' is the stored token;
// its human label is "Legacy pool".
export const QS_MODES = Object.freeze({
  mine: 'My patients',
  mine_plus: 'Mine + colleagues',
  all: 'Everyone',
  unassigned: 'Legacy pool',
});

/**
 * Normalize a requested mode to a canonical, VALID token. 'legacy' is accepted
 * as an alias for the persisted 'unassigned' (see the persistence-parity note
 * above); any unknown value returns null so callers can reject it.
 * @param {any} mode
 * @returns {'mine'|'mine_plus'|'all'|'unassigned'|null}
 */
export function normalizeMode(mode) {
  if (mode === 'legacy') return 'unassigned';
  return Object.prototype.hasOwnProperty.call(QS_MODES, mode) ? mode : null;
}

/**
 * Role default (legacy loadQueueScope): assignment-capable staff (admins,
 * coordinators) land on 'all'; a plain nurse lands on 'mine'.
 * @param {string|null|undefined} role
 * @returns {{mode: 'mine'|'mine_plus'|'all'|'unassigned', extra: string[]}}
 */
export function defaultScope(role) {
  return { mode: canAssign(role) ? 'all' : 'mine', extra: [] };
}

/**
 * Parse a persisted scope blob against the role default. Only a well-formed
 * object with a known mode wins; anything else (garbage, stale mode, non-array
 * extra) falls back to the default. `extra` is sanitised to string ids. Pure —
 * tested directly. Exact legacy validation.
 * @param {string|null} raw the localStorage string (or null)
 * @param {string|null|undefined} role for the fallback default
 * @returns {{mode: 'mine'|'mine_plus'|'all'|'unassigned', extra: string[]}}
 */
export function parseScope(raw, role) {
  const def = defaultScope(role);
  if (!raw) return def;
  try {
    const p = JSON.parse(raw);
    const mode = p && normalizeMode(p.mode);
    if (!mode) return def;
    const extra = Array.isArray(p.extra) ? p.extra.filter((x) => typeof x === 'string') : [];
    return { mode, extra };
  } catch {
    return def;
  }
}

/**
 * Does a case owner (uuid, or null/undefined for unassigned) fall inside a
 * scope, for a given signed-in user? Pure — the heart of F-SCOPE-02. Exact
 * legacy scopeMatch, with self-id and scope passed in instead of read from
 * globals so it tests without a session.
 * @param {string|null|undefined} assignedTo case owner id
 * @param {{mode: string, extra?: string[]}} scope
 * @param {string|null|undefined} selfId the signed-in user's id
 * @returns {boolean}
 */
export function scopeMatch(assignedTo, scope, selfId) {
  const mode = (scope && scope.mode) || 'mine';
  if (mode === 'all') return true;
  if (mode === 'unassigned') return !assignedTo;
  if (mode === 'mine_plus') {
    return assignedTo === selfId || (!!assignedTo && !!scope.extra && scope.extra.includes(assignedTo));
  }
  return assignedTo === selfId; // 'mine'
}

/**
 * Compact pill label (legacy scopeLabel): mine_plus with colleagues shows the
 * count, everything else its mode label.
 * @param {{mode: string, extra?: string[]}} scope
 * @returns {string}
 */
export function scopeLabel(scope) {
  const mode = (scope && scope.mode) || 'mine';
  if (mode === 'mine_plus' && scope.extra && scope.extra.length) return `Mine + ${scope.extra.length}`;
  return QS_MODES[mode] || QS_MODES.mine;
}

/**
 * Count of the Legacy pool = ownerless, non-closed cases (legacy renderScopeMenu /
 * renderUnassignedBanner). When the full case set is available, count directly;
 * the queue owner-map path is handled by queue.js's own ownerless count.
 * @param {any[]} cases case rows with assigned_to
 * @param {(c: any) => boolean} isClosed the isClosedCase predicate
 * @returns {number}
 */
export function legacyPoolCount(cases, isClosed) {
  return (cases || []).filter((c) => !c.assigned_to && !isClosed(c)).length;
}

// ─── Stateful get/set (localStorage-backed, legacy key) ────────────────────
// A per-instance cache mirrors legacy `queueScope`: read once, reuse, rewrite
// on set. Keyed by the signed-in user so a shared terminal never leaks one
// nurse's scope into another's session.

/** @type {{uid: string|null, scope: any}} */
let _cache = { uid: null, scope: null };

/** Test-only: drop the in-memory scope cache. @returns {void} */
export function _resetScope() {
  _cache = { uid: null, scope: null };
}

function storageOf(storage) {
  return storage || /** @type {any} */ (globalThis).localStorage;
}

function keyFor(uid) {
  return SCOPE_KEY_PREFIX + (uid || 'anon');
}

/**
 * Current scope for the signed-in user. Reads localStorage once per user, then
 * serves the cache (legacy getQueueScope lazy-load). Never throws.
 * @param {string|null|undefined} selfId
 * @param {string|null|undefined} role for the first-time default
 * @param {any} [storage] injectable for tests
 * @returns {{mode: 'mine'|'mine_plus'|'all'|'unassigned', extra: string[]}}
 */
export function getScope(selfId, role, storage) {
  const uid = selfId || 'anon';
  if (_cache.uid === uid && _cache.scope) return _cache.scope;
  let raw = null;
  try {
    raw = storageOf(storage).getItem(keyFor(selfId));
  } catch {
    /* storage unavailable — fall back to the default below */
  }
  _cache = { uid, scope: parseScope(raw, role) };
  return _cache.scope;
}

/**
 * Persist a whole scope object for the signed-in user (legacy saveQueueScope).
 * The mode is normalised so a caller passing 'legacy' still stores the
 * v_20-compatible 'unassigned'. Updates the cache and never throws.
 * @param {string|null|undefined} selfId
 * @param {{mode: string, extra?: string[]}} scope
 * @param {any} [storage]
 * @returns {{mode: 'mine'|'mine_plus'|'all'|'unassigned', extra: string[]}} the stored scope
 */
export function setScope(selfId, scope, storage) {
  const mode = normalizeMode(scope && scope.mode) || 'mine';
  const rawExtra = scope && scope.extra;
  const extra = Array.isArray(rawExtra) ? rawExtra.filter((x) => typeof x === 'string') : [];
  const next = { mode, extra };
  _cache = { uid: selfId || 'anon', scope: next };
  try {
    storageOf(storage).setItem(keyFor(selfId), JSON.stringify(next));
  } catch {
    /* storage unavailable — the in-memory cache still applies this session */
  }
  return next;
}

/**
 * Set just the mode, keeping any existing colleague selection (legacy
 * setQueueScope). Returns the new scope (or the unchanged one for a bad mode).
 * @param {string|null|undefined} selfId
 * @param {string} mode
 * @param {string|null|undefined} role
 * @param {any} [storage]
 * @returns {{mode: string, extra: string[]}}
 */
export function setScopeMode(selfId, mode, role, storage) {
  const m = normalizeMode(mode);
  const cur = getScope(selfId, role, storage);
  if (!m) return cur;
  return setScope(selfId, { mode: m, extra: cur.extra }, storage);
}
